if (!customElements.get('custom-product-card')) {
  class CustomProductCard extends HTMLElement {
    constructor() {
      super();
      this.container = this.querySelector('.product-card-wrapper');
      this.atcButton = this.querySelector('.add-to-cart-btn');
      this.qtySelector = this.querySelector('.quantity-selector');
      this.qtyDisplay = this.querySelector('.qty-number');
      this.variantButtons = this.querySelectorAll('.variant-btn');
      this.variantInput = this.querySelector('.variant-hidden-input');
      this.variantNameDisplay = this.querySelector('[data-variant-name-display]');
      
      this.currentId = this.variantInput ? this.variantInput.value : this.querySelector('input[name="id"]')?.value;
      this.isLoading = false;
    }

    connectedCallback() {
      this.init();
    }

    setLoading(loading) {
      this.isLoading = loading;
      loading ? this.container?.classList.add('is-loading') : this.container?.classList.remove('is-loading');
    }

    refreshDrawerHTML(sections) {
      if (!sections) return;
      const drawer = document.querySelector('cart-drawer');
      if (drawer && sections['cart-drawer']) {
        const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const newInner = html.querySelector('.drawer__inner');
        const currentInner = drawer.querySelector('.drawer__inner');
        if (newInner && currentInner) currentInner.innerHTML = newInner.innerHTML;
        drawer.classList.remove('is-empty');
      }
    }

    init() {
      if (!this.atcButton) return;

      // 1. ADD TO CART: Optimistic update
      this.atcButton.addEventListener('click', async (e) => {
        e.preventDefault();
        if (this.isLoading) return;

        // Instant UI change
        this.atcButton.classList.add('hidden');
        this.qtySelector.classList.remove('hidden');
        this.qtyDisplay.textContent = '1';

        await this.addToCart();
      });

      // 2. QUANTITY BUTTONS: Optimistic update
      this.querySelectorAll('.qty-btn').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          if (this.isLoading) return;
          
          const delta = e.currentTarget.dataset.action === 'plus' ? 1 : -1;
          const currentQty = parseInt(this.qtyDisplay.textContent || '0', 10);
          const newQty = Math.max(0, currentQty + delta);

          // Instant UI change
          if (newQty === 0) {
            this.atcButton.classList.remove('hidden');
            this.qtySelector.classList.add('hidden');
          } else {
            this.qtyDisplay.textContent = newQty;
          }

          await this.updateQuantity(newQty);
        });
      });

      // 3. VARIANT BUTTONS: Normal sync (needs server check for existing qty)
      this.variantButtons.forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          if (this.isLoading || btn.classList.contains('active')) return;
          
          this.variantButtons.forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          if (this.variantNameDisplay) this.variantNameDisplay.textContent = btn.textContent.trim();
          
          this.currentId = btn.dataset.variantId;
          if (this.variantInput) this.variantInput.value = this.currentId;
          
          const priceEl = this.querySelector('[data-price-display]');
          const comparePriceEl = this.querySelector('[data-compare-price-display]');

          if (priceEl) {
            priceEl.textContent = btn.dataset.price || '';
          }

          if (comparePriceEl) {
            const price = parseInt(btn.dataset.priceRaw || 0, 10);
            const comparePrice = parseInt(btn.dataset.comparePriceRaw || 0, 10);

            if (comparePrice > price) {
              comparePriceEl.textContent = btn.dataset.comparePrice || '';
              comparePriceEl.classList.remove('hidden');
            } else {
              comparePriceEl.classList.add('hidden');
            }
          }

          this.syncUI(true); 
        });
      });

      // 4. TOAST CLICK
      document.querySelector('#open-cart-btn')?.addEventListener('click', (e) => {
        e.preventDefault();
        this.openDrawer();
      });

      // 5. GLOBAL INTERCEPTOR: Sync all cards when cart changes elsewhere
      if (!window.cartFetchIntercepted) {
        window.cartFetchIntercepted = true;
        const originalFetch = window.fetch;
        window.fetch = (...args) => originalFetch(...args).then((response) => {
          const url = args[0];
          if (typeof url === 'string' && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear'))) {
            // Background sync (silent)
            document.querySelectorAll('custom-product-card').forEach(card => card.syncUI(false));
          }
          return response;
        });
      }

      this.syncUI(false);
    }

    async syncUI(showLoader = false) {
      if (showLoader) this.setLoading(true);
      try {
        const res = await fetch(`${window.Shopify.routes.root}cart.js?v=${new Date().getTime()}`);
        const cart = await res.json();
        
      // --- UPDATED COUNTER LOGIC ---
          const noirCounter = document.querySelector('#noir-counter');
          if (noirCounter) {
            noirCounter.textContent = cart.item_count;
          }

        // Update Toast
        const toastCount = document.querySelector('#toastCount');
        const cartToast = document.querySelector('#cartToast');
        if (toastCount && cartToast) {
          toastCount.textContent = `${cart.item_count} Items in cart`;
          cart.item_count > 0 ? cartToast.classList.add('show') : cartToast.classList.remove('show');
        }

        const item = cart.items.find(i => String(i.id) === String(this.currentId));
        const qty = item ? item.quantity : 0;

        // Sync visual state if optimistic update was slightly off
        if (qty > 0) {
          this.atcButton.classList.add('hidden');
          this.qtySelector.classList.remove('hidden');
          this.qtyDisplay.textContent = qty;
        } else {
          this.atcButton.classList.remove('hidden');
          this.qtySelector.classList.add('hidden');
          const currentVariant = this.querySelector(`.variant-btn[data-variant-id="${this.currentId}"]`);
          this.atcButton.disabled = currentVariant ? currentVariant.dataset.available !== 'true' : false;
          if (this.atcButton.disabled) this.atcButton.textContent = 'Out Of Stock';
          else this.atcButton.textContent = 'Add to Cart';
        }
      } catch (e) {
        console.error("Sync Error", e);
      } finally {
        if (showLoader) this.setLoading(false);
      }
    }

    async addToCart() {
      this.setLoading(true);
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: this.currentId, quantity: 1, sections: 'cart-drawer' })
        });
        const data = await response.json();
        this.refreshDrawerHTML(data.sections);
      } catch (err) {
        this.syncUI(false); // Revert UI if error
      } finally {
        this.setLoading(false);
      }
    }

    async updateQuantity(newQty) {
      this.setLoading(true);
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: String(this.currentId), quantity: newQty, sections: 'cart-drawer' })
        });
        const data = await response.json();
        this.refreshDrawerHTML(data.sections);
      } catch (err) {
        this.syncUI(false); // Revert UI if error
      } finally {
        this.setLoading(false);
      }
    }

    openDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        if (typeof drawer.open === 'function') drawer.open();
        else drawer.classList.add('active');
      }
    }
  }
  customElements.define('custom-product-card', CustomProductCard);
}